import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { 
  FEATURE_FLAGS, 
  getFeatureFlagStatus, 
  CURRENT_VERSION, 
  APP_VERSIONS,
  isInDevMode
} from "@/config/featureFlags";
import { Settings, Eye, EyeOff } from "lucide-react";
import { LanguageSelector } from './LanguageSelector';

export const FeatureFlagDebugger = () => {
  const [isOpen, setIsOpen] = useState(false);
  const status = getFeatureFlagStatus();

  // Only show in development AND when in dev mode
  if (!import.meta.env.DEV || !status || !isInDevMode()) return null;

  return (
    <div className="fixed top-4 right-4 z-50">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100"
      >
        <Settings size={16} className="mr-1" />
        Feature Flags [DEV]
        {isOpen ? <EyeOff size={14} className="ml-1" /> : <Eye size={14} className="ml-1" />}
      </Button>

      {isOpen && (
        <Card className="absolute top-12 right-0 w-80 max-h-96 overflow-y-auto bg-white border-2 border-yellow-300 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>YS v{CURRENT_VERSION} - Feature Flags [DEV]</span>
              <Badge variant="outline" className="text-xs">
                {status.environment}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Language Selector */}
            {FEATURE_FLAGS.LANGUAGE_TRANSLATION.enabled && (
              <div className="p-2 bg-blue-50 rounded border">
                <div className="text-xs font-medium mb-2">Language Translation (v1.3.0)</div>
                <LanguageSelector />
              </div>
            )}

            {/* Version Info */}
            <div className="text-xs space-y-1">
              <div className="font-medium">Current Version Features:</div>
              {APP_VERSIONS.find(v => v.version === CURRENT_VERSION)?.features.map(feature => (
                <Badge key={feature} variant="secondary" className="text-xs mr-1">
                  {feature}
                </Badge>
              ))}
            </div>

            {/* Feature Flags */}
            <div className="space-y-2">
              <div className="text-xs font-medium">Feature Flags:</div>
              {Object.values(FEATURE_FLAGS).map((flag) => (
                <div key={flag.key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex-1">
                    <div className="text-xs font-medium">{flag.name}</div>
                    <div className="text-xs text-gray-500">{flag.description}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <Badge variant="outline" className="text-xs">
                        v{flag.version}
                      </Badge>
                      {'environment' in flag && (
                        <Badge 
                          variant={flag.environment === 'development' ? 'secondary' : 'default'} 
                          className="text-xs"
                        >
                          {flag.environment}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={flag.enabled}
                    disabled
                    className="ml-2"
                  />
                </div>
              ))}
            </div>

            {/* Instructions */}
            <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
              <div className="font-medium mb-1">Development Mode Only</div>
              <div>This debugger is hidden in production builds</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
